import { Component, computed, input, OnChanges, signal, SimpleChanges } from "@angular/core";
import { SvgIconComponent } from "@kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { CheckboxInputComponent } from "@kernel/form/modules/inputs/simple-inputs/components/checkbox-input/checkbox-input.component";
import { TranslatePipe } from "@ngx-translate/core";
import { CustomInput } from "@kernel/form/classes/custom-input";
import { NgClass } from "@angular/common";
import {
  isPropertyMatchOptionActive,
  PROPERTY_MATCH_OPTIONS,
  togglePropertyMatchOption,
  TPropertyMatchOption,
} from "@app/core/filtering/helpers/property-match.helper";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
  selector: "app-property-match-options-input",
  imports: [SvgIconComponent, CheckboxInputComponent, TranslatePipe, NgClass],
  templateUrl: "./property-match-options-input.component.html",
  styleUrl: "./property-match-options-input.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PropertyMatchOptionsInputComponent,
      multi: true,
    },
  ],
})
export class PropertyMatchOptionsInputComponent extends CustomInput<number> implements OnChanges {
  /**
   * Reactive bitmask for multiple selection using signals.
   * Each bit in `reactiveValue` represents a selection state of a match option.
   */
  reactiveValue = signal(0);

  protected readonly MATCH_OPTIONS = PROPERTY_MATCH_OPTIONS;

  /** Computed signal: true if 'Liked Me' is selected */
  likedMeSelected = computed(() => isPropertyMatchOptionActive(this.reactiveValue(), this.MATCH_OPTIONS.LIKED_ME));

  /** Computed signal: true if 'Mutual Interest' is selected */
  mutualInterestSelected = computed(() => isPropertyMatchOptionActive(this.reactiveValue(), this.MATCH_OPTIONS.MUTUAL_INTEREST));

  /** Computed signal: true if 'Best Match' is selected */
  bestMatchSelected = computed(() => isPropertyMatchOptionActive(this.reactiveValue(), this.MATCH_OPTIONS.BEST_MATCH));

  /** Input to determine the layout orientation of the component (horizontal or vertical) */
  horizontal = input(false);

  /**
   * Lifecycle hook that responds to changes in input properties.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes["value"]) {
      this.reactiveValue.set(this.value ?? 0);
    }
  }

  /**
   * Toggles the specified property match option in the current value.
   *
   * This method updates the internal value, the reactive signal, and notifies
   * any external listeners via the `onChange` callback.
   *
   * @param option - The property match option to toggle (one of `TPropertyMatchOption` / `PROPERTY_MATCH_OPTIONS` values).
   */
  toggleOption(option: TPropertyMatchOption) {
    this.value = togglePropertyMatchOption(this.reactiveValue(), option);
    this.reactiveValue.set(this.value);
    this.onChange(this.value);
  }

  /**
   * Sets the value of the reactive bitmask from an external source.
   * Updates the internal signal and calls the parent `writeValue`.
   * @param value - The numeric bitmask representing selected options.
   */
  override writeValue(value: number) {
    this.reactiveValue.set(value);
    super.writeValue(value);
  }
}
